import lodash from "lodash";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useGroupsLeanFilterContext } from "../../../providers/groups/groups-lean-filter-provider";
import { IGroupLean } from "../../../domain/group/group-lean";
import { IPaginationMeta } from "../../../domain/pagination/pagination-meta";
import { IPaginationMetaModel } from "../../../models/utils/pagination-meta.model";
import { IGetGroupsLeanFilter } from "../../../repositories/models/group/get-group.model";
import { useGetGroupsLeanFiltersQuery } from "../../../repositories/queries/group/get-groups-lean.query";

interface IGroupContextType {
  isLoading: boolean;
  tablePagination: IPaginationMetaModel;
  setTablePagination: (pagination: IPaginationMetaModel) => void;
  filter: IGetGroupsLeanFilter;
  setFilter: (filters: IGetGroupsLeanFilter) => void;
  groups: IGroupLean[];
  refetchGroups: () => Promise<void>;
  isFilterDirty: boolean;
  resetFilter: () => void;
}

const GroupContext = createContext<IGroupContextType>(
  {} as IGroupContextType,
);

export const GroupProvider: React.FC<React.PropsWithChildren> = (props) => {
  const value = useGroupProvider();
  return <GroupContext.Provider value={value}>{props.children}</GroupContext.Provider>;
};

export const useGroupContext = () => {
  return useContext(GroupContext);
};

const useGroupProvider = (): IGroupContextType => {
  const { filter, setFilter } = useGroupsLeanFilterContext();

  const [tablePagination, setTablePagination] = useState<IPaginationMeta>({
    numberOfRecords: 0,
    pageSize: 50,
    page: 0,
    numberOfPages: 0,
  });
  const { data, isLoading, refetch } = useGetGroupsLeanFiltersQuery(
    tablePagination,
    filter,
  );

  const groups = data?.items ?? [];

  useEffect(() => {
    const numberOfRecords = data?.meta?.numberOfRecords;
    if (numberOfRecords !== undefined) {
      setTablePagination((prev) => {
        if (prev.numberOfRecords === numberOfRecords) return prev;
        return { ...prev, numberOfRecords };
      });
    }
  }, [data]);

  const refetchGroups = async () => {
    await refetch();
  };

  const isFilterDirty = useMemo(
    () =>
      filter.groupType !== undefined || !lodash.isEmpty(filter.nameQuery) || !lodash.isEmpty(filter.erpQuery),
    [filter],
  );

  const resetFilter = () =>
    setFilter({
      nameQuery: undefined,
      erpQuery: undefined,
      groupType: undefined,
    });

  return {
    isLoading,
    tablePagination,
    setTablePagination,
    filter,
    setFilter,
    groups,
    refetchGroups,
    isFilterDirty,
    resetFilter,
  } satisfies IGroupContextType;
};
